import { supabase } from "../config/supabase";
import { PatientData } from "../types/supabase";
import { User } from "./UserService";

type LifecycleAction = "archived" | "restored" | "hard_deleted";

interface LifecycleContext {
  patient: PatientData;
  user: User;
  clientId: string;
  reason?: string;
}

export interface PatientLifecycleEvent {
  eventId: string;
  clientId: string;
  patientId: string;
  action: LifecycleAction;
  reason?: string | null;
  actorUserId?: string | null;
  actorDisplayName?: string | null;
  actorEmail?: string | null;
  actorIp?: string | null;
  actorUserAgent?: string | null;
  snapshot?: Record<string, unknown> | null;
  eventCreatedAt: string;
}

interface LifecycleResult {
  operation: LifecycleAction;
  hasHistory: boolean;
  completedAppointmentsCount: number;
  medicalRecordsCount: number;
  totalAppointmentsCount: number;
}

const IP_SERVICES = [
  "https://api64.ipify.org?format=json",
  "https://api.ipify.org?format=json",
  "https://ifconfig.me/all.json",
];

const fetchJsonWithTimeout = async (url: string, timeoutMs: number): Promise<any | null> => {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetch(url, { signal: controller.signal });
    if (!response.ok) return null;
    return await response.json();
  } catch {
    return null;
  } finally {
    window.clearTimeout(timeoutId);
  }
};

const resolvePublicIp = async (): Promise<string | null> => {
  for (const service of IP_SERVICES) {
    const payload = await fetchJsonWithTimeout(service, 2500);
    const ip = payload?.ip || payload?.ip_addr || null;
    if (typeof ip === "string" && ip.trim()) {
      return ip.trim();
    }
  }

  return null;
};

export class PatientLifecycleService {
  static async assertPatientBelongsToClient(patientId: string, clientId: string): Promise<void> {
    const { count, error } = await supabase
      .from("patient_clients")
      .select("patientId", { count: "exact", head: true })
      .eq("patientId", patientId)
      .eq("clientId", clientId);

    if (error) throw error;
    if (!count) {
      throw new Error("Paciente não encontrado para este tenant.");
    }
  }

  static async setPatientCareActive(
    patientId: string,
    clientId: string,
    active: boolean,
  ): Promise<void> {
    await this.assertPatientBelongsToClient(patientId, clientId);

    const { error } = await supabase
      .from("patients")
      .update({
        patientLifecycleStatus: active ? "active" : "inactive",
        patientUpdatedAt: new Date().toISOString(),
      })
      .eq("patientId", patientId);

    if (error) throw error;
  }

  static async getHistoryCounts(patientId: string) {
    const [{ count: completedAppointmentsCount, error: completedAppointmentsError }, { count: medicalRecordsCount, error: medicalRecordsError }, { count: totalAppointmentsCount, error: totalAppointmentsError }] = await Promise.all([
      supabase
        .from("appointments")
        .select("appointmentId", { count: "exact", head: true })
        .eq("patientId", patientId)
        .eq("appointmentStatus", "completed"),
      supabase
        .from("medical_records")
        .select("recordId", { count: "exact", head: true })
        .eq("patientId", patientId),
      supabase
        .from("appointments")
        .select("appointmentId", { count: "exact", head: true })
        .eq("patientId", patientId),
    ]);

    if (completedAppointmentsError) throw completedAppointmentsError;
    if (medicalRecordsError) throw medicalRecordsError;
    if (totalAppointmentsError) throw totalAppointmentsError;

    const completed = completedAppointmentsCount ?? 0;
    const records = medicalRecordsCount ?? 0;
    const total = totalAppointmentsCount ?? 0;

    return {
      hasHistory: completed > 0 || records > 0,
      completedAppointmentsCount: completed,
      medicalRecordsCount: records,
      totalAppointmentsCount: total,
    };
  }

  static async archiveOrDelete(context: LifecycleContext): Promise<LifecycleResult> {
    const { patient, user, clientId, reason } = context;
    const actorIp = await resolvePublicIp();
    const actorUserAgent = typeof navigator !== "undefined" ? navigator.userAgent : null;
    await this.assertPatientBelongsToClient(patient.patientId, clientId);

    // Fluxo local em código (sem dependência de RPC no banco)
    const history = await this.getHistoryCounts(patient.patientId);
    const snapshot = {
      patient,
      metadata: {
        completedAppointmentsCount: history.completedAppointmentsCount,
        medicalRecordsCount: history.medicalRecordsCount,
        totalAppointmentsCount: history.totalAppointmentsCount,
      },
    };

    if (history.hasHistory) {
      const { error: archiveError } = await supabase
        .from("patients")
        .update({
          patientLifecycleStatus: "archived",
          patientArchivedAt: new Date().toISOString(),
          patientArchivedBy: user.userId,
          patientArchivedReason: reason?.trim() || null,
          patientUpdatedAt: new Date().toISOString(),
        })
        .eq("patientId", patient.patientId);

      if (archiveError) throw archiveError;

      const { error: eventError } = await supabase.from("patient_lifecycle_events").insert({
        clientId,
        patientId: patient.patientId,
        action: "archived",
        reason: reason?.trim() || null,
        actorUserId: user.userId,
        actorDisplayName: user.userDisplayName || null,
        actorEmail: user.userEmail || null,
        actorIp: actorIp || null,
        actorUserAgent: actorUserAgent,
        snapshot,
      });

      if (eventError) throw eventError;

      return {
        operation: "archived",
        hasHistory: true,
        completedAppointmentsCount: history.completedAppointmentsCount,
        medicalRecordsCount: history.medicalRecordsCount,
        totalAppointmentsCount: history.totalAppointmentsCount,
      };
    }

    const { error: eventError } = await supabase.from("patient_lifecycle_events").insert({
      clientId,
      patientId: patient.patientId,
      action: "hard_deleted",
      reason: reason?.trim() || null,
      actorUserId: user.userId,
      actorDisplayName: user.userDisplayName || null,
      actorEmail: user.userEmail || null,
      actorIp: actorIp || null,
      actorUserAgent: actorUserAgent,
      snapshot,
    });

    if (eventError) throw eventError;

    const { error: deleteError } = await supabase
      .from("patients")
      .delete()
      .eq("patientId", patient.patientId);

    if (deleteError) throw deleteError;

    return {
      operation: "hard_deleted",
      hasHistory: false,
      completedAppointmentsCount: history.completedAppointmentsCount,
      medicalRecordsCount: history.medicalRecordsCount,
      totalAppointmentsCount: history.totalAppointmentsCount,
    };
  }

  static async restoreArchived(context: LifecycleContext): Promise<void> {
    const { patient, user, clientId, reason } = context;
    const actorIp = await resolvePublicIp();
    const actorUserAgent = typeof navigator !== "undefined" ? navigator.userAgent : null;
    await this.assertPatientBelongsToClient(patient.patientId, clientId);

    // Fluxo local em código (sem dependência de RPC no banco)
    const { error: restoreError } = await supabase
      .from("patients")
      .update({
        patientLifecycleStatus: "active",
        patientArchivedAt: null,
        patientArchivedBy: null,
        patientArchivedReason: null,
        patientUpdatedAt: new Date().toISOString(),
      })
      .eq("patientId", patient.patientId);

    if (restoreError) throw restoreError;

    const { error: eventError } = await supabase.from("patient_lifecycle_events").insert({
      clientId,
      patientId: patient.patientId,
      action: "restored",
      reason: reason?.trim() || null,
      actorUserId: user.userId,
      actorDisplayName: user.userDisplayName || null,
      actorEmail: user.userEmail || null,
      actorIp: actorIp || null,
      actorUserAgent: actorUserAgent,
      snapshot: { patientId: patient.patientId },
    });

    if (eventError) throw eventError;
  }

  static async getLifecycleEvents(patientId: string, clientId: string): Promise<PatientLifecycleEvent[]> {
    const { data, error } = await supabase
      .from("patient_lifecycle_events")
      .select("*")
      .eq("patientId", patientId)
      .eq("clientId", clientId)
      .order("eventCreatedAt", { ascending: false })
      .limit(30);

    if (error) throw error;
    return (data || []) as PatientLifecycleEvent[];
  }
}
